// src/components/layout/Navbar.js
import React, {
  useState, useContext, useEffect, useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiMenu, HiX } from 'react-icons/hi';
import { AuthContext } from '../../context/AuthContext';
import UserSelectBox from '../UserSelectBox';
import { useService } from '../../context/ServiceContext';
import { fetchUserDetails } from '../../services/apiService';
import { useSidebar } from '../../context/SidebarContext';

const menuItemsByRole = {
  super_admin: [
    { name: 'Registro de Usuarios', path: '/userregistrationform' },
    { name: 'Administración de Usuarios', path: '/adminusermanagement' },
    { name: 'Editar Usuarios', path: '/superadminmanagement' },
    { name: 'Reporte de Servicios', path: '/user-services-report' },
  ],
  admin: [],
  user: [],
};

function Navbar() {
  const {
    authTokens, clearAuthTokens, impersonatedUserId, stopImpersonation, userRole, menuUpdateTrigger,
  } = useContext(AuthContext);
  const { selectedService } = useService();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inspectorInfo, setInspectorInfo] = useState(null);
  const isAuthenticated = !!authTokens;
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch inspector info when user is a station
  useEffect(() => {
    const loadInspectorInfo = async () => {
      if (authTokens && (authTokens.userRole === 'user' || (impersonatedUserId && userRole === 'user'))) {
        try {
          const userIdToFetch = impersonatedUserId || authTokens.userId;
          const userDetails = await fetchUserDetails(userIdToFetch, authTokens);
          if (userDetails.admin_info && userDetails.admin_info.length > 0) {
            setInspectorInfo(userDetails.admin_info[0]);
          }
        } catch (error) {
          console.error('Error al cargar la información del inspector:', error);
        }
      }
    };

    loadInspectorInfo();
  }, [authTokens, impersonatedUserId, userRole]);

  const isInCategoryPage = () => {
    const categoryPaths = [
      'anexo30y31ano',
      'dictamendediseno',
      'nom016',
      'inspeccion30y31ano',
    ];
    return categoryPaths.some((path) => location.pathname.toLowerCase().includes(path.toLowerCase()));
  };

  const handleReloadMenu = useCallback(() => {
    console.log('Reloading menu...');
    const role = authTokens ? authTokens.userRole : 'guest';

    // Si es super_admin, no mostrar items de servicio

    console.log(`Menu items set for role: ${role}`);
  }, [authTokens, selectedService]);

  useEffect(() => {
    const getUserRole = () => {
      if (impersonatedUserId) {
        return 'user';
      }
      if (authTokens) {
        return authTokens.userRole;
      }
      return 'guest';
    };

    const role = getUserRole();

    // Si es super_admin, no mostrar items de servicio

    console.log(`Menu items set for role: ${role}`);
  }, [authTokens, impersonatedUserId, userRole, menuUpdateTrigger, selectedService]);

  // Efecto adicional para limpiar los items cuando se navega al dashboard o cambia el servicio seleccionado

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/dashboard');
    setTimeout(() => {
      handleReloadMenu();
    }, 2000);
  };

  const handleSignOut = () => {
    clearAuthTokens();
    navigate('/');
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleMenuClick = (path, requiresConfirmation) => {
    if (requiresConfirmation) {
      const confirmed = window.confirm(
        'Al abrir esta sección, la aplicación se ralentizará y la carga puede tardar entre 2 y 5 minutos. ¿Desea continuar?',
      );
      if (!confirmed) {
        return;
      }
    }
    navigate(path);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    console.log('Component mounted, triggering menu reload...');
    handleReloadMenu();
  }, [handleReloadMenu, menuUpdateTrigger]);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-[#0962b1] shadow-sm h-16 flex items-center"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between">
          {/* Left section – hamburger (mobile) + Admin Actions (desktop) */}
          <div className="flex items-center">
            <button
              type="button"
              onClick={toggleSidebar}
              className="mr-2 p-2 rounded-md text-white hover:bg-blue-700 focus:outline-none"
              aria-label={isSidebarOpen ? 'Cerrar menú lateral' : 'Abrir menú lateral'}
            >
              {isSidebarOpen ? (
                <HiX className="h-6 w-6" />
              ) : (
                <HiMenu className="h-6 w-6" />
              )}
            </button>
            {!isInCategoryPage() && userRole === 'super_admin' && !impersonatedUserId && (
              <div className="hidden md:flex items-center space-x-4">
                {menuItemsByRole.super_admin.map((item) => (
                  <button
                    key={item.path}
                    className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
                    type="button"
                    onClick={() => handleMenuClick(item.path, item.requiresConfirmation)}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Right section - User Actions */}
          <div className="flex items-center space-x-4">
            {['admin', 'super_admin'].includes(authTokens?.userRole) && (
              <div className="hidden md:block">
                <UserSelectBox />
              </div>
            )}
            {authTokens?.userRole === 'super_admin' && impersonatedUserId && (
              <button
                onClick={handleStopImpersonation}
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium
                transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                Salir de Estación
              </button>
            )}

            {isAuthenticated && (
              <div className="relative user-dropdown">
                <button
                  type="button"
                  onClick={toggleDropdown}
                  className="flex items-center space-x-2 text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
                >
                  <div className="flex flex-col items-end">
                    <span>{authTokens.username}</span>
                    {inspectorInfo && (() => {
                      const telStr = String(inspectorInfo.admin_telefono ?? '').trim();
                      return (
                        <>
                          <span className="text-xs text-gray-300">
                            {`Inspector: ${inspectorInfo.admin_username}`}
                          </span>
                          {telStr ? (
                            <span className="text-xs text-gray-300">
                              {`Teléfono del inspector: ${telStr}`}
                            </span>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                  <svg
                    className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Cerrar Sesión
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
